import "@app/styles/index.css";

import { GTM_ID } from "@elements/GAData/GADataConstants";
import { VWO_ACCOUNT_ID } from "@helper/getEnvVariables";
import useSessionRefresh from "@hooks/useSessionRefresh";
import { Route } from "@route-types/../+types/root";
import { LightboxProvider } from "@sphtech/idpf-lightbox-react";
import { VwoSmartCode } from "@sphtech/sph-web-ui/utils/vwo";
import NeuronProvider from "@web2/providers/NeuronProvider";
import { StrictMode, useEffect } from "react";
import TagManager from "react-gtm-module";
import {
  isRouteErrorResponse,
  Outlet,
  ScrollRestoration,
  useLocation,
} from "react-router";

import { BaseLayout, OauthLayout } from "./Layouts";
import HeadScriptProvider from "./providers/HeadScriptProvider";

// Wrapper component that sets up the HTML shell and global scripts
export function Layout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  if (location.pathname.startsWith("/oauth/callback")) {
    return <OauthLayout>{children}</OauthLayout>;
  }

  return <BaseLayout>{children}</BaseLayout>;
}

// Main application component
export default function App() {
  const location = useLocation();

  useEffect(() => {
    if (location.pathname.startsWith("/oauth/callback")) {
      return;
    }

    if (typeof window !== "undefined" && window.__tablaGtmInitialized) {
      return;
    }

    const initGtm = () => {
      if (typeof window !== "undefined" && window.__tablaGtmInitialized) {
        return;
      }
      TagManager.initialize({
        gtmId: GTM_ID,
        auth: "",
        preview: "",
        dataLayerName: "dataLayer",
      });
      if (typeof window !== "undefined") {
        window.__tablaGtmInitialized = true;
      }
    };

    // Defer GTM until the browser is idle to avoid blocking TTI/TBT
    if (typeof window !== "undefined") {
      if ("requestIdleCallback" in window) {
        requestIdleCallback(initGtm, { timeout: 3000 });
      } else {
        setTimeout(initGtm, 3000);
      }
    }
  }, [location.pathname]);

  if (location.pathname.startsWith("/oauth/callback")) {
    return <Outlet />;
  }

  return (
    <StrictMode>
      <HeadScriptProvider />
      {/* VWO Async SmartCode - injected early to minimise experiment flicker.
            Renders nothing until VITE_VWO_ACCOUNT_ID is a valid numeric id. */}
      <VwoSmartCode accountId={VWO_ACCOUNT_ID} />
      <NeuronProvider>
        <ScrollRestoration />
        <LightboxProvider>
          <SessionRefreshHandler />
          <div id="MyPageOverlay"></div>
          <div id="postitial_holder"></div>
          <Outlet />
        </LightboxProvider>
      </NeuronProvider>
    </StrictMode>
  );
}

// Runs in the browser and catches errors that occur during rendering of React components on the client.
// I.e Catches that occur during CLIENT side routing
// Errors during rendering, lifecycle methods, and constructors in the React component tree.
export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  if (isRouteErrorResponse(error)) {
    return (
      <>
        <h1>is Route Error Response</h1>
        <h1>
          {error.status} {error.statusText}
        </h1>
        <p>{error.data}</p>
      </>
    );
  } else if (error instanceof Error) {
    return (
      <div>
        <h1>Error</h1>
        <p>{error.message}</p>
        <p>The stack trace is:</p>
        <pre>{error.stack}</pre>
      </div>
    );
  } else {
    return <h1>Unknown Error</h1>;
  }
}

function SessionRefreshHandler() {
  useSessionRefresh();
  return null;
}

declare global {
  interface Window {
    __tablaGtmInitialized?: boolean;
  }
}
